import { FunctionComponent, useState } from "react";

import {
  Box,
  Flex,
  SystemProps,
  Text,
  LegacyText,
  UnderlineButton,
} from "flicket-ui";

import styled from "styled-components";
import { FlexProps } from "styled-system";
import { Icon, IconProps } from "~components/common";
import Image from "~components/common/Image";
import { ExtendedFile } from "~graphql/sdk";

type StyledPaymentListItemProps = SystemProps & {
  isActive: boolean;
};

const StyledPaymentListItem = styled(Flex)<StyledPaymentListItemProps>`
  position: relative;
  min-height: 70px;
  padding: 12px 0 12px 16px;
  cursor: pointer;
  align-items: center;

  border: 1px solid ${(p) => p.theme.colors.N200};
  border-radius: ${(p) => p.theme.radii.sm};

  transition: box-shadow 0.25s ease-out, border 0.15s ease-out;

  ${(p) =>
    p.isActive &&
    `
      border: 1px solid ${p.theme.colors.P300};
      box-shadow: 0 0 0 1px ${p.theme.colors.P300},
        ${p.theme.shadows.sm};
  `}

  &:hover {
    border: 1px solid ${(p) => p.theme.colors.P300};
    box-shadow: 0 0 0 1px ${(p) => p.theme.colors.P300},
      ${(p) => p.theme.shadows.button};
  }

  input[type="radio"] {
    cursor: pointer;

    + label {
      cursor: pointer;
      display: flex;
      align-items: flex-start;
      justify-content: center;
      flex-direction: column;

      width: 100%;
      padding-left: 14px;
    }
  }
`;

interface PaymentListItemProps extends FlexProps {
  type: "full" | "partial";
  title: string;
  value: string;
  isActive: boolean;
  onChange: (value: string, _?: string) => void;
  image?: ExtendedFile;
  icon?: IconProps["icon"];
  subtitle?: string;
  InfoModal?: FunctionComponent<{ isOpen: boolean; onClose: () => void }>;
  imageMaxWidth?: string;
}

const PaymentListItem = ({
  type = "full",
  title,
  value,
  isActive,
  onChange,
  image,
  icon,
  subtitle,
  InfoModal,
  imageMaxWidth = "64px",
}: PaymentListItemProps) => {
  const [isOpen, setOpen] = useState(false);
  const id = `payment-item:${type}:${value}`;

  return (
    <StyledPaymentListItem my={2} isActive={isActive}>
      <input
        type="radio"
        id={id}
        checked={isActive}
        onChange={(e) => onChange(e.target.value)}
        value={value}
      />
      <label htmlFor={id}>
        <Flex
          flexDir="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <Flex flexDir="column">
            <Flex>
              <LegacyText
                fontSize={3}
                lineHeight="normal"
                fontWeight="demiBold"
                color="N800"
              >
                {title}
              </LegacyText>
            </Flex>
            {(subtitle || InfoModal) && (
              <Flex>
                <LegacyText
                  color="N600"
                  fontSize={2}
                  alignItems="baseline"
                  display="block"
                  mt="1/2"
                  {...(!image && { pr: 2 })}
                >
                  {subtitle}
                  {InfoModal && InfoModal !== null && (
                    <>
                      <UnderlineButton
                        lineHeight="normal"
                        fontWeight="regular"
                        color="N600"
                        ml={subtitle ? "1/2" : 0}
                        onClick={() => setOpen(true)}
                        display="inline"
                        verticalAlign={"baseline"}
                      >
                        More Info
                      </UnderlineButton>
                    </>
                  )}
                </LegacyText>
              </Flex>
            )}
          </Flex>
          {image && (
            <Image image={image} alt={title} width={imageMaxWidth} mr={2} />
          )}
          {icon && (
            <Icon icon={icon} fontSize={"64px" as any} mr={2} maxHeight={40} />
          )}
        </Flex>
      </label>
      {InfoModal && (
        <InfoModal isOpen={isOpen} onClose={() => setOpen(false)} />
      )}
    </StyledPaymentListItem>
  );
};

export default PaymentListItem;
